// ----------------------------------------------------------------------- externos
import { useEffect, useMemo, useState, useContext } from "react";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

// ----------------------------------------------------------------------- internos
import Swal from "sweetalert2";

import { useAuth } from "../../auth/AuthContext";
import { initCache } from "../../services/cache";
import { aplicarPrefs, leerPrefs } from "../../functions/preferencias";
import { traerConteos } from "../../services/conteos";
import { misPermisos } from "../../services/permisos";
import {
  SECCIONES,
  formatearCuenta,
  puedeVer,
  universoDe,
} from "./secciones";
import Cabeza from "./Cabeza";
import Icono, { iconoDeUniverso } from "./Icono";
import SiSeRompe from "./SiSeRompe";
import PanelesFlotaBot from "./PanelesFlotaBot";
import { OPCIONES_DE_TEMA } from "./temasUat";
import { TemaUatContexto } from "./SelectorDeTemas";
import Preferencias from "./Preferencias";
import { CampanaSalidas } from "../vigilancia/SalidaPendiente";

import "../../assets/css/hub.css";
import "../../assets/css/componentes.css";
import "../../assets/css/temas.css";
import "../../assets/css/temas-clubes.css";
import "../../assets/css/armazon-uat.css";

/* ARMAZÓN DE PRUEBA (UAT) — el rediseño "Industrial" sin rail: barra superior +
 * pestañas + barra de estado, con los 13 temas de `temas.css` elegibles arriba.
 *
 * Es un shell PARALELO al de producción (`Layout.jsx`), que no se toca. Vive
 * sólo en la rama de UAT para probar la estructura nueva con datos reales, sin
 * riesgo para el hub que usa la gente. Las PANTALLAS de adentro siguen con sus
 * tokens actuales hasta que se migren; acá lo que cambia es el armazón y el
 * juego de colores del chrome.
 */

/* El tema (`data-tema`) y el conmutador de armazón los maneja `LayoutSwitcher`,
 * que es común a los dos armazones. Acá el tema llega por props sólo para pintar
 * el selector de la barra superior. */
/* ADÓNDE LLEVA UNA PESTAÑA. Las pantallas de FlotaBot no tienen ruta propia en
 * el hub: viven en el armazón, en `/panel/<id>`, y su `ruta` es la de FlotaBot
 * (`/movimientos`). El rail ya lo sabía (`Rail.jsx`); esta barra usaba `ruta`
 * a secas, así que «Movimientos diarios» —y cualquier otra de FlotaBot—
 * navegaba a una dirección que el hub no tiene y quedaba en blanco. */
export const destinoDe = (item) =>
  item.donde === "flotabot" ? `/panel/${item.id}` : item.ruta;

/* ¿Esta pestaña es la pantalla en la que estoy? */
const esLaRuta = (item, pathname) => {
  const d = (destinoDe(item) || "").split("?")[0];
  return !!d && (pathname === d || pathname.startsWith(d + "/"));
};

const LayoutUAT = ({ tema, onTema }) => {
  const vista = useContext(TemaUatContexto);
  const { fullUser, logout } = useAuth();
  const [prefsAbiertas, setPrefsAbiertas] = useState(false);
  // La misma pregunta que hace la cabecera del menú del costado (`Header`).
  const salir = async () => {
    const r = await Swal.fire({
      title: "¿Cerrar sesión?",
      text: "Vas a salir de la aplicación",
      icon: "warning",
      showCancelButton: true,
      backdrop: "rgba(0, 0, 0, 0.8)",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "var(--acc, #1B4D8F)",
      cancelButtonColor: "transparent",
      customClass: { cancelButton: "swal-cancel-btn" },
    });
    if (r.isConfirmed) await logout();
  };
  const { pathname, search } = useLocation();
  const navegar = useNavigate();

  const [permisos, setPermisos] = useState(null);
  const [cuentas, setCuentas] = useState({});
  const [abrirSectores, setAbrirSectores] = useState(false);
  // El grupo que la persona abrió a mano para mirar sus pestañas, sin haber
  // entrado todavía a ninguna. Al cambiar de pantalla manda la pantalla.
  const [grupoElegido, setGrupoElegido] = useState(null);
  useEffect(() => setGrupoElegido(null), [pathname]);

  useEffect(() => {
    if (!fullUser) return;
    initCache();
  }, [fullUser]);

  useEffect(() => {
    if (!fullUser) return;
    let vivo = true;
    traerConteos().then((c) => vivo && setCuentas(c));
    misPermisos().then((p) => vivo && setPermisos(p));
    return () => {
      vivo = false;
    };
  }, [fullUser]);

  // Las preferencias de la persona (acento, fondo) se siguen aplicando; el TEMA
  // del rediseño lo aplica LayoutSwitcher por `data-tema`.
  useEffect(() => {
    if (!fullUser) return;
    aplicarPrefs(leerPrefs(fullUser.id));
  }, [fullUser]);

  /* Los sectores y sus pestañas salen del MISMO catálogo que el rail
   * (`SECCIONES`), filtrado por permisos. Así el UAT muestra exactamente lo que
   * la persona puede abrir. */
  const grupos = useMemo(
    () =>
      SECCIONES.map((g) => ({
        ...g,
        items: g.items.filter((i) => !i.oculto && puedeVer(i, permisos)),
      })).filter((g) => g.items.length > 0),
    [permisos],
  );

  const universos = useMemo(() => {
    const orden = [];
    const porNombre = new Map();
    for (const g of grupos) {
      if (!g.universo) continue;
      if (!porNombre.has(g.universo)) {
        porNombre.set(g.universo, { items: [], grupos: [] });
        orden.push(g.universo);
      }
      const u = porNombre.get(g.universo);
      u.items.push(...g.items);
      u.grupos.push({ grupo: g.grupo || g.universo, items: g.items });
    }
    return orden.map((u) => ({ universo: u, ...porNombre.get(u) }));
  }, [grupos]);

  const universoActivo = universoDe(pathname, undefined, search);
  const sectorActivo =
    universos.find((u) => u.universo === universoActivo) || universos[0] || null;

  /* LA BARRA EN DOS FILAS (Igna, 06/10: «la barra donde están todos los
   * sectores es interminable»). Tráfico tiene adentro Operación, Satelital,
   * Combustible, Urea y Documentación: treinta y pico de pestañas en una sola
   * fila que había que desplazar para encontrar algo.
   *
   * Los grupos YA ESTÁN en el catálogo —son los subtítulos del rail—, así que
   * no se parte ningún sector: arriba va una fila corta con los grupos y abajo
   * sólo las pestañas del que está abierto. Un sector de un solo grupo sigue
   * con una fila, como antes. */
  const gruposDelSector = sectorActivo?.grupos || [];
  const grupoDeLaRuta = gruposDelSector.find((g) =>
    g.items.some((i) => esLaRuta(i, pathname)),
  );
  const grupoAbierto =
    gruposDelSector.find((g) => g.grupo === grupoElegido) ||
    grupoDeLaRuta ||
    gruposDelSector[0] ||
    null;
  const hayGrupos = gruposDelSector.length > 1;
  const tabs = hayGrupos ? grupoAbierto?.items || [] : sectorActivo?.items || [];

  const irASector = (u) => {
    setAbrirSectores(false);
    const primero = u.items[0];
    if (primero) navegar(destinoDe(primero));
  };

  const iniciales = (fullUser?.nombres || fullUser?.mail || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

  const enPanel = pathname.startsWith("/panel/");
  const seccionDelPanel = enPanel ? pathname.slice("/panel/".length) : null;

  return (
    <div className="uat-hub">
      <header className="uat-top">
        <button className="uat-logo" onClick={() => navegar("/")} title="Inicio">
          Fi
        </button>

        <button
          className="uat-sector"
          data-ico={sectorActivo ? iconoDeUniverso(sectorActivo.universo) : undefined}
          onClick={() => setAbrirSectores((v) => !v)}
          title="Cambiar de sector"
        >
          <small>SECTOR</small>
          <b>
            {/* El ícono del sector, el mismo del rail: en la barra no había
                ninguno, y es por donde se reconoce un sector de un vistazo. */}
            {sectorActivo && (
              <Icono
                nombre={iconoDeUniverso(sectorActivo.universo)}
                size={19}
                className="uat-sector-ico"
              />
            )}
            {sectorActivo?.universo || "—"} <span className="fl">▾</span>
          </b>
        </button>

        <div className="uat-search">
          <input placeholder="Interno, patente, chofer o ticket   ·   Ctrl K" />
        </div>

        <div className="uat-tema">
          <select
            value={tema}
            onChange={(e) => onTema(e.target.value)}
            title="Tema"
          >
            {OPCIONES_DE_TEMA.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* VOLVER AL MENÚ DEL COSTADO. Quien pasaba a «Arriba» no tenía cómo
            volver (Igna, 08/10). También se puede desde Preferencias, pero
            esto es un toque y está a la vista. */}
        {vista?.setArmazon && (
          <button
            type="button"
            className="uat-vista"
            onClick={() => vista.setArmazon("rail")}
          >
            Menú al costado
          </button>
        )}

        {/* PREFERENCIAS Y SALIR. Esta vista no las tenía: para cambiar el fondo
            o cerrar sesión había que volver primero al menú del costado. */}
        <CampanaSalidas className="uat-icono" />
        <button
          type="button"
          className="uat-icono"
          onClick={() => setPrefsAbiertas(true)}
          aria-label="Preferencias"
          title="Preferencias"
        >
          ⚙
        </button>
        <div className="uat-user">
          <span className="ini">{iniciales}</span>
          <span>{fullUser?.nombres || "Vos"}</span>
        </div>
        <button
          type="button"
          className="uat-icono uat-icono--salir"
          onClick={salir}
          aria-label="Cerrar sesión"
          title="Cerrar sesión"
        >
          ✕
        </button>
      </header>

      {abrirSectores && (
        <div className="uat-sectores">
          {universos.map((u) => (
            <button
              key={u.universo}
              className={u.universo === universoActivo ? "activo" : ""}
              onClick={() => irASector(u)}
              /* Para los temas que tienen emblemas propios (hoy Industrial):
                 la hoja cambia el ícono de línea por el emblema según cuál
                 sea, y el que no tiene emblema se queda con el de línea. */
              data-ico={iconoDeUniverso(u.universo)}
              /* Un nombre largo («Automatizaciones») va con la letra un poco
                 más chica: en los temas de letra ancha no entraba al lado del
                 emblema. */
              data-largo={
                u.universo.length > 14 ? "mucho" : u.universo.length > 11 ? "" : undefined
              }
            >
              <Icono nombre={iconoDeUniverso(u.universo)} size={20} />
              <span className="uat-sector-nombre">{u.universo}</span>
            </button>
          ))}
        </div>
      )}

      {hayGrupos && (
        <div className="uat-grupos" role="tablist" aria-label="Grupos del sector">
          {gruposDelSector.map((g) => (
            <button
              key={g.grupo}
              type="button"
              role="tab"
              aria-selected={g === grupoAbierto}
              className={
                "uat-grupo" +
                (g === grupoAbierto ? " uat-grupo--abierto" : "") +
                (g === grupoDeLaRuta ? " uat-grupo--aca" : "")
              }
              onClick={() => setGrupoElegido(g.grupo)}
            >
              {g.grupo}
              <span className="n">{g.items.length}</span>
            </button>
          ))}
        </div>
      )}

      <nav className="uat-tabs" aria-label="Secciones del sector">
        {tabs.map((it) => (
          <NavLink
            key={it.id || it.ruta}
            to={destinoDe(it)}
            className={({ isActive }) => "uat-tab" + (isActive ? " activo" : "")}
          >
            {it.corto || it.label}
            {it.cuenta && cuentas[it.cuenta] ? (
              <span className="cuenta">
                {formatearCuenta(cuentas[it.cuenta])}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      <main className="uat-main">
        <Cabeza />
        {enPanel ? null : (
          <SiSeRompe key={pathname}>
            <Outlet context={{ cuentas, permisos }} />
          </SiSeRompe>
        )}
        <PanelesFlotaBot seccion={seccionDelPanel} verComoMail={null} permisos={permisos} />
      </main>

      <footer className="uat-estado">
        {/* Acá decía «■ SATELITAL OK» y «UAT · REDISEÑO INDUSTRIAL», los dos
            escritos a mano: el primero afirmaba un estado que nadie medía. */}
        <span className="sep">CTRL K BUSCAR · ? ATAJOS</span>
      </footer>

      <Preferencias
        abierto={prefsAbiertas}
        onCerrar={() => setPrefsAbiertas(false)}
        idPersona={fullUser?.id}
        nombrePersona={fullUser?.nombres}
      />
    </div>
  );
};

export default LayoutUAT;
